import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
} from '@mui/material';
import { useState } from 'react';
import CustomButton from '../../MUI_custom_component/Button/CustomButton';
import LocationMapSelector from './LocationMapSelector';
import AddressFormModal from './AddressFormModal';
import ReusableModal from '../../MUI_custom_component/Modal/CustomModal';
import ConfirmationDialog from '../../MUI_custom_component/Modal/ConfirmationDialog';
import { deleteAddressAction, reverseGeocodeAction } from '../APIs/action';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { toast } from 'react-toastify';
import { formatCompleteAddress } from '../../../utils/settings';
import MapIcon from '../Assets/MapIcon';
const DeliveryLocationCard = ({
  onSetLocation,
  addressDetails = null,
  fetchAddressDetails = () => {},
  isPwa = false,
  showMobileSizedCard = false,
  companyEsiID = null,
  non_reward= false,
  onAddressAdded = () => {}
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [addressFormModalOpen, setAddressFormModalOpen] = useState(false);
  const [mapCenter, setMapCenter] = useState(null);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [preloadedAddressData, setPreloadedAddressData] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAddressBeingEdited, setIsAddressBeingEdited] = useState(false);

  const handleSetLocationClick = () => {
    if (navigator.geolocation) {
      setIsLocationLoading(true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setMapCenter([latitude, longitude]);
          setMarkerPosition([latitude, longitude]);

          try {
            const { data, error, message } = await reverseGeocodeAction(
              latitude,
              longitude,
            );
            if (!error) {
              setPreloadedAddressData(data);
            } else {
              toast.error(message);
              setPreloadedAddressData(null);
            }
          } catch (error) {
            setPreloadedAddressData(null);
          }

          setLocationModalOpen(true);
          setIsLocationLoading(false);
        },
        (error) => {
          setIsLocationLoading(false);
          setPreloadedAddressData(null);

          // Check if user denied permission
          if (error.code === error.PERMISSION_DENIED) {
            // Use default coordinates for display and open address form
            setMapCenter([28.7041, 77.1025]); // Default to Delhi
            setMarkerPosition([28.7041, 77.1025]);
            setAddressFormModalOpen(true);
          } else {
            // For other errors, use default coordinates and open the location map selector
            setMapCenter([28.7041, 77.1025]); // Default to Delhi
            setMarkerPosition([28.7041, 77.1025]);
            setLocationModalOpen(true);
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 600000,
        },
      );
    } else {
      // Set default coordinates and preloaded data to null for manual input
      setPreloadedAddressData(null);
      setMapCenter([28.7041, 77.1025]); // Default to Delhi
      setMarkerPosition([28.7041, 77.1025]);
      setLocationModalOpen(true);
    }
  };

  const handleMapClick = (e) => {
    const { lat, lng } = e.latlng;
    setMarkerPosition([lat, lng]);
    setMapCenter([lat, lng]);
  };

  const handleCurrentLocation = (location) => {
    const { lat, lng } = location.latlng;
    setMapCenter([lat, lng]);
    setMarkerPosition([lat, lng]);
  };

  const handleLocationSubmit = (addressData) => {
    setLocationModalOpen(false);
    
    // Call onAddressAdded to refresh the address list
    if (onAddressAdded) {
      onAddressAdded();
    }

    //commented as onSetLocation was not doing anything in Parent caller.
    // if (onSetLocation) {
    //   onSetLocation(addressData);
    // }
  };

  const handleLocationModalClose = () => {
    setLocationModalOpen(false);
    fetchAddressDetails();
    
    // Call onAddressAdded to refresh the address list
    if (onAddressAdded) {
      onAddressAdded();
    }
  };

  const handleAddressFormModalClose = () => {
    setAddressFormModalOpen(false);
  };

  const handleAddressFormSave = (addressData) => {
    setAddressFormModalOpen(false);
    // if (onSetLocation) {
    //   onSetLocation(addressData);
    // }
    fetchAddressDetails();
    
    // Call onAddressAdded to refresh the address list
    if (onAddressAdded) {
      onAddressAdded();
    }
  };

  const handleDeleteClick = (address) => {
    if (!address?.id) {
      toast.error('Address ID not found');
      return;
    }
    setAddressToDelete(address);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!addressToDelete?.id) {
      setDeleteDialogOpen(false);
      return;
    }

    setIsDeleting(true);
    try {
      const { data, error, message } = await deleteAddressAction(
        addressToDelete.id,
      );

      if (!error) {
        toast.success('Address deleted successfully');
        // Refresh the address list
        await fetchAddressDetails();
        setDeleteDialogOpen(false);
        setAddressToDelete(null);
      } else {
        toast.error(message || 'Failed to delete address');
      }
    } catch (error) {
      console.error('Error deleting address:', error);
      toast.error('An error occurred while deleting the address');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAddressToDelete(null);
  };

  const handleEditClick = (address) => {
    // For edit mode, always open AddressFormModal with existing address details
    setIsAddressBeingEdited(true);
    setAddressFormModalOpen(true);
  };

  return (
    <Box
      sx={{
        borderRadius: '16px',
        border: !addressDetails || addressDetails?.length === 0 ? `1px solid #995A00` : 'none',
        background: theme.palette.background.default,
        zIndex: 2,
      }}
    >
      {!addressDetails ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: showMobileSizedCard || isMobile ? 'column' : 'row',
              padding: 2,
              gap: {xs: 0.5, md: 1},
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                flex: 1,
                width: showMobileSizedCard || isMobile ? '100%' : 'auto',
                gap: 2,
              }}
            >
              <MapIcon width={64} height={64} />
              <Box sx={{ display: 'flex', flexDirection: 'column'}}>
                <Typography variant='h2' >
                  Set your delivery location
                </Typography>
                <Typography variant='body1'>
                  To ensure timely reward delivery, please set your precise
                  location.
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <CustomButton
                variant='filled'
                color='primary'
                label='Set Delivery Location'
                size={isMobile ? 'small' : 'medium'}
                onClick={handleSetLocationClick}
                disabled={isLocationLoading}
              />
            </Box>
          </Box>
      ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              padding: 2,
            }}
          >
            <Box sx={{ width: '80%', ml: 0.5, mr: 2 }}>
              {addressDetails?.landmark ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                  }}
                >
                  <PlaceOutlinedIcon
                    sx={{ height: '24px', width: '24px', marginRight: '2px' }}
                    color='#666666'
                  />
                  <Typography variant='h2'>
                    {addressDetails?.landmark}
                  </Typography>
                </Box>
              ) : null}
              <Typography variant={'body1'}>
                {formatCompleteAddress(addressDetails)}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  marginTop: '8px',
                }}
              >
                <Typography
                  variant={'labelM'}
                  sx={{ color: '#666666' }}
                >
                  Whatsapp Phone:
                </Typography>
                <Typography
                  variant={'body1'}
                  sx={{ marginLeft: '5px' }}
                >
                  {addressDetails?.phone_no}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <IconButton onClick={() => handleEditClick(addressDetails)}>
                <EditOutlinedIcon style={{ width: 18, height: 18 }} />
              </IconButton>
              {!isPwa && (
                <IconButton onClick={() => handleDeleteClick(addressDetails)}>
                  <DeleteOutlineOutlinedIcon
                    style={{ width: 18, height: 18 }}
                  />
                </IconButton>
              )}
            </Box>
          </Box>
      )}

      <ReusableModal
        open={locationModalOpen}
        onClose={handleLocationModalClose}
        showHeader={false}
        maxHeight={isMobile ? '100vh' : '589px'}
        topMobile={'0%'}
        topDesktop={'10%'}
        scrollable={false}
        pt={0}
        px={0}
        pb={0}
        maxWidth={isMobile ? '100%' : '618px'}
        centerAlign={!isMobile}
      >
        <LocationMapSelector
          mapCenter={mapCenter}
          markerPosition={markerPosition}
          onMapClick={handleMapClick}
          onCurrentLocation={handleCurrentLocation}
          onConfirmLocation={handleLocationSubmit}
          onCloseModal={handleLocationModalClose}
          handleSaveAddress={fetchAddressDetails}
          preloadedAddressData={preloadedAddressData}
          isPwa={isPwa}
          non_reward={non_reward}
        />
      </ReusableModal>

      <ReusableModal
        open={addressFormModalOpen}
        onClose={handleAddressFormModalClose}
        showHeader={false}
        maxHeight={isMobile ? '100vh' : '589px'}
        topMobile={'0%'}
        topDesktop={'10%'}
        scrollable={false}
        pt={0}
        px={0}
        pb={0}
        maxWidth={isMobile ? '100%' : '618px'}
        centerAlign={!isMobile}
      >
        <AddressFormModal
          onBack={handleAddressFormModalClose}
          onSaveAddress={handleAddressFormSave}
          isAddressBeingEdited={isAddressBeingEdited}
          addressDetails={addressDetails}
          companyEsiID={companyEsiID}
          coords={{ lat: null, lng: null }} //Delhi default coordinates if no location permissions given by user
          non_reward={non_reward}
          onAddressAdded={onAddressAdded}
        />
      </ReusableModal>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title='Are you sure you want to delete this address?'
        message='The address will be removed from your profile.'
        confirmText='Yes'
        cancelText='Cancel'
        isLoading={isDeleting}
        non_reward={non_reward}
      />
    </Box>
  );
};

export default DeliveryLocationCard;
